// Low-level WebAuthn encoding helpers and ceremony wrappers. The server speaks
// base64url (webauthn-ruby's options JSON); the browser speaks ArrayBuffers.

export function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const padding = (4 - (base64.length % 4)) % 4;
  const binary = atob(base64 + "=".repeat(padding));
  const buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    buffer[i] = binary.charCodeAt(i);
  }
  return buffer.buffer;
}

// Serialize a PublicKeyCredential (from create or get) into JSON the server
// can parse with WebAuthn::Credential.from_create / from_get.
function encodeCredential(credential) {
  const response = {};
  const r = credential.response;

  if (r.attestationObject !== undefined) {
    response.attestationObject = bufferToBase64url(r.attestationObject);
    response.clientDataJSON = bufferToBase64url(r.clientDataJSON);
  } else {
    response.authenticatorData = bufferToBase64url(r.authenticatorData);
    response.clientDataJSON = bufferToBase64url(r.clientDataJSON);
    response.signature = bufferToBase64url(r.signature);
    if (r.userHandle) {
      response.userHandle = bufferToBase64url(r.userHandle);
    }
  }

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response,
  };
}

export async function createCredential(optionsJson) {
  const publicKey = {
    ...optionsJson,
    challenge: base64urlToBuffer(optionsJson.challenge),
    user: { ...optionsJson.user, id: base64urlToBuffer(optionsJson.user.id) },
    excludeCredentials: (optionsJson.excludeCredentials || []).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  const credential = await navigator.credentials.create({ publicKey });
  return encodeCredential(credential);
}

export async function getCredential(optionsJson, { mediation, signal } = {}) {
  const publicKey = {
    ...optionsJson,
    challenge: base64urlToBuffer(optionsJson.challenge),
    allowCredentials: (optionsJson.allowCredentials || []).map((c) => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  const credential = await navigator.credentials.get({
    publicKey,
    mediation,
    signal,
  });
  return encodeCredential(credential);
}
